import { useAppSelector } from "../../app/hooks";
import { selectHotel } from "../../features/hotel/hotelSlice";
import {
  isRoomCategoryAvailable,
  roomCategoryPriceFrom,
} from "../../helpers/roomCategory";
import { useEffect, useState } from "react";
import { type IHotelRoomCategory } from "../../features/hotel/hotelInterface";
import ReservationCardCategoryName from "./ReservationCardCategoryName";
import { Trans, useTranslation } from "react-i18next";
import { camalize } from "../../helpers/language";
import FormatPricing from "../Helpers/FormatPricing";
import Button from "../Button";
import RoomCategoryRatesModal from "./RoomCategoryRatesModal";
import Modal from "../Helpers/Modal";
import Chevron from "../Svg/Chevron";
import ReservationSlider from "./ReservationSlider";

interface Tag {
  q: string;
  a: string;
}

export default function RoomCategoryCard(props: {
  roomCategory: IHotelRoomCategory;
}) {
  const { roomCategory } = props;

  const hotel = useAppSelector(selectHotel);
  const { t } = useTranslation();
  const [showAccommodationModal, setShowAccommodationModal] = useState<
    string | null
  >(null);
  const [showRoomCategoryRatesModal, setShowRoomCategoryRatesModal] = useState<
    string | null
  >(null);
  const [tags, setTags] = useState<string[]>([]);
  const [images, setImages] = useState<{ src: string; alt: string }[]>([]);
  const [translationPath, setTranslationPath] = useState<string>("");
  const [roomCategoryName, setRoomCategoryName] = useState<string>("");
  const [openQuestion, setOpenQuestion] = useState<number | undefined>(
    undefined,
  );
  /**
   * Set tags based on room category and language
   */
  useEffect(() => {
    const roomCategoryName =
      roomCategory?.Name[hotel.Culture.LanguageCode] ||
      roomCategory?.Name["en-US"] ||
      "";
    setRoomCategoryName(roomCategoryName);
    const path =
      "accommodation." + hotel.HotelCode + "." + camalize(roomCategoryName);

    setTranslationPath(path);
    setImages(t(path + ".images", { returnObjects: true }) as []);
    const arrayTags = t("accommodation.tags." + camalize(roomCategoryName), {
      returnObjects: true,
    }) as [];

    const allTags = arrayTags.concat(
      t("accommodation." + hotel.HotelCode + ".tags", {
        returnObjects: true,
      }) as [],
    );
    setTags(allTags);
  }, [roomCategory, hotel.Culture.LanguageCode]);

  const pricing = roomCategoryPriceFrom({ roomCategoryId: roomCategory.Id });
  const isAvailable = isRoomCategoryAvailable(roomCategory.Id);

  return (
    <>
      <div
        className={`bg-custom-100 rounded-xl text-black text-fluid-base ${
          isAvailable ? "" : "opacity-50"
        }`}
      >
        <div className="flex flex-col w-full">
          {roomCategory && images.length > 1 && (
            <div
              className="relative cursor-pointer h-48 h-64 "
              onClick={() => setShowAccommodationModal(roomCategory.Id)}
            >
              <div className="absolute top-1 right-1 bg-black/50 text-white text-fluid-xs rounded-md px-2 py-1">
                +{images.length - 1}
              </div>

              <img
                className="rounded-t-lg object-cover w-full h-full"
                src={
                  t(translationPath + ".imagePath") +
                  t(translationPath + ".images.0.src")
                }
                alt={t(translationPath + ".images.0.alt")}
              />
            </div>
          )}
          <div className="p-4 flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <div className="text-fluid-lg">
                <ReservationCardCategoryName
                  names={roomCategory?.Name ?? {}}
                  languageCode={hotel.Culture.LanguageCode}
                  rename={true}
                  showIcon={false}
                />
              </div>
              {isAvailable ? (
                <div>
                  {t("scenes.accommodation.price_from")}{" "}
                  <FormatPricing pricing={pricing} />{" "}
                  {t("scenes.summary.perNight")}
                </div>
              ) : (
                <div>{t("scenes.accommodation.not_available")}</div>
              )}
            </div>

            <div
              className="text-gray-700 cursor-pointer leading-thight"
              onClick={() => setShowAccommodationModal(roomCategory.Id)}
            >
              {t(translationPath + ".description")}{" "}
              <span className="underline">
                {t("scenes.accommodation.more_info")}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 text-fluid-sm">
              {Array.isArray(tags) &&
                tags.slice(0, 3).map((tag, index) => (
                  <span
                    key={index}
                    className="px-1.5 py-0 rounded-sm bg-custom-300 text-nowrap cursor-pointer"
                    onClick={() => setShowAccommodationModal(roomCategory.Id)}
                  >
                    {tag}
                  </span>
                ))}
              {tags.length > 3 && (
                <span
                  className="px-1.5 py-0 rounded-md bg-custom-300 text-nowrap cursor-pointer"
                  onClick={() => setShowAccommodationModal(roomCategory.Id)}
                >
                  +{tags.length - 4}
                </span>
              )}
            </div>
          </div>
        </div>

        {isAvailable && (
          <div className="border-t-2 border-custom-200 border-dashed px-2 py-3">
            <Button
              classes="primary"
              size="large"
              text={t("scenes.accommodation.add")}
              onClick={() => setShowRoomCategoryRatesModal(roomCategory.Id)}
            />
          </div>
        )}
      </div>

      <RoomCategoryRatesModal
        show={showRoomCategoryRatesModal == roomCategory.Id}
        onClick={() => setShowRoomCategoryRatesModal(null)}
        roomCategoryId={roomCategory.Id}
      />

      <Modal
        onClick={() => {
          setShowAccommodationModal(null);
          setOpenQuestion(undefined);
          return false;
        }}
        show={showAccommodationModal == roomCategory.Id}
        title={t(translationPath + ".title")}
        padding={0}
      >
        <ReservationSlider
          images={t(translationPath + ".images", { returnObjects: true }) as []}
          path={t(translationPath + ".imagePath")}
          peek={0.1}
        />

        <div className="flex flex-col gap-4 p-5 pt-0">
          <span className="text-fluid-h2">{t(translationPath + ".title")}</span>

          <div className="flex flex-wrap gap-2 text-fluid-sm">
            {Array.isArray(tags) &&
              tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-1.5 py-0 rounded-sm bg-custom-300 text-nowrap cursor-pointer"
                >
                  {tag}
                </span>
              ))}
          </div>

          <p>{t(translationPath + ".description")}</p>

          <div className="flex flex-col gap-2">
            <div className="text-fluid-h2">
              {t("scenes.accommodation.questions")}
            </div>
            <div className="divide-y-1 divide-black/10">
              {Array.isArray(
                t("accommodation.faq." + camalize(roomCategoryName), {
                  returnObjects: true,
                }) as Tag[],
              ) &&
                (
                  t("accommodation.faq." + camalize(roomCategoryName), {
                    returnObjects: true,
                  }) as Tag[]
                ).map((item, index) => (
                  <div
                    key={index}
                    className={`flex flex-col gap-2 text-fluid-sm py-3 cursor-pointer`}
                    onClick={() =>
                      setOpenQuestion(
                        openQuestion === index ? undefined : index,
                      )
                    }
                  >
                    <div className="flex justify-between items-center">
                      <span>{t(item.q)}</span>
                      <Chevron
                        className={`size-4 ${
                          openQuestion === index ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    </div>
                    <p
                      className={`text-gray-500 mt-2 ${
                        openQuestion === index ? "show" : "hidden"
                      }`}
                    >
                      <Trans
                        i18nKey={item.a}
                        components={{
                          ul: <ul />,
                          li: <li />,
                          br: <br />,
                        }}
                      />
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
